import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Twitter, ArrowRight, Check, Mail } from "lucide-react";
import { WhatsAppIcon, WHATSAPP_URL } from "./WhatsAppButton";
import { useApp } from "../context/AppContext";
import { categories } from "../mock";

const Footer = () => {
  const { t } = useApp();
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);

  const subscribe = (e) => {
    e.preventDefault();
    if (email.trim()) {
      setDone(true);
      setEmail("");
      setTimeout(() => setDone(false), 3000);
    }
  };

  const helpLinks = [
    { label: "FAQ", to: "/page/faq" }, { label: t("delivery"), to: "/page/livraison-retours" }, { label: t("contactNav"), to: "/contact" }, { label: "Guide des tailles", to: "/page/guide-tailles" },
  ];
  const companyLinks = [{ label: "Notre histoire", to: "/page/notre-histoire" }, { label: "Boutiques", to: "/page/boutiques" }, { label: "Carrières", to: "/page/carrieres" }, { label: "Durabilité", to: "/page/durabilite" }];
  const legalLinks = [{ label: "CGV", to: "/page/cgv" }, { label: "Confidentialité", to: "/page/confidentialite" }, { label: "Cookies", to: "/page/cookies" }, { label: "Mentions légales", to: "/page/mentions-legales" }];

  return (
    <footer className="bg-[#1b1b1b] text-[#e8e3da] mt-24">
      {/* Newsletter */}
      <div className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-14 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h3 className="font-serif-lux text-3xl md:text-4xl mb-3">{t("newsletterTitle")}</h3>
            <p className="text-white/60 text-sm max-w-md leading-relaxed">{t("newsletterText")}</p>
          </div>
          <form onSubmit={subscribe} className="flex gap-0 max-w-md w-full md:ml-auto">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("emailPlaceholder")}
              className="flex-1 bg-transparent border border-white/25 border-r-0 px-4 py-3.5 text-sm outline-none focus:border-[#b08d57] transition-colors placeholder:text-white/40"
            />
            <button
              type="submit"
              className="bg-[#b08d57] hover:bg-[#9a7a48] transition-colors px-6 flex items-center gap-2 text-sm font-medium tracking-wide text-white"
            >
              {done ? <Check className="h-4 w-4" strokeWidth={2} /> : <>{t("subscribe")}<ArrowRight className="h-4 w-4" strokeWidth={1.5} /></>}
            </button>
          </form>
        </div>
      </div>

      {/* Links */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-14 grid grid-cols-2 md:grid-cols-5 gap-8">
        <div className="col-span-2">
          <span className="font-serif-lux text-2xl">Amour<span className="text-[#b08d57]">duluxe</span></span>
          <p className="text-white/50 text-sm mt-4 max-w-xs leading-relaxed">{t("footerAboutText")}</p>
          <a href="mailto:contact@amourduluxe.com" className="inline-flex items-center gap-2 mt-4 text-sm text-white/70 hover:text-[#b08d57] transition-colors">
            <Mail className="h-4 w-4" strokeWidth={1.5} /> contact@amourduluxe.com
          </a>
          <div className="flex gap-4 mt-6">
            {[
              { Icon: Instagram, url: "https://instagram.com/amourduluxe", label: "Instagram" },
              { Icon: Facebook, url: "https://facebook.com/amourduluxe", label: "Facebook" },
              { Icon: Twitter, url: "https://twitter.com/amourduluxe", label: "Twitter" },
            ].map(({ Icon, url, label }, i) => (
              <a key={i} href={url} target="_blank" rel="noopener noreferrer" aria-label={label} data-testid={`social-${label.toLowerCase()}`} className="text-white/60 hover:text-[#b08d57] transition-colors">
                <Icon className="h-5 w-5" strokeWidth={1.5} />
              </a>
            ))}
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" data-testid="social-whatsapp" className="text-white/60 hover:text-[#25D366] transition-colors">
              <WhatsAppIcon className="h-5 w-5" />
            </a>
          </div>
        </div>

        <FooterCol title={t("footerHelp")} links={helpLinks} />
        <FooterCol title={t("footerCompany")} links={companyLinks} />
        <FooterCol title={t("footerLegal")} links={legalLinks} />
      </div>

      {/* Bottom */}
      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/40">
          <span>© {new Date().getFullYear()} Amourduluxe. Tous droits réservés.</span>
          <div className="flex gap-3">
            {categories.map((c) => (
              <Link key={c.id} to={`/c/${c.id}`} className="hover:text-[#b08d57] transition-colors">{t(c.key)}</Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

const FooterCol = ({ title, links }) => (
  <div>
    <h4 className="text-xs uppercase tracking-[0.15em] text-white/80 mb-4">{title}</h4>
    <ul className="space-y-2.5">
      {links.map((l, i) => (
        <li key={i}>
          {l.to ? (
            <Link to={l.to} className="text-sm text-white/50 hover:text-[#b08d57] transition-colors">{l.label}</Link>
          ) : (
            <a href="#" className="text-sm text-white/50 hover:text-[#b08d57] transition-colors">{l.label}</a>
          )}
        </li>
      ))}
    </ul>
  </div>
);

export default Footer;
